import React from "react";
import styled, { css } from "styled-components";
import { Body, Header } from "../../../styleguide/atoms/index.js";
import * as styles from "../../../styleguide/styles/Stylesheet.js";

const RulesWrapper = styled.div`
  width: 100%;
  max-width: ${styles.width_lg};
  display: flex;
  flex-direction: column;
  padding: 80px ${styles.spacing_standard} ${styles.spacing_standard};
  overflow-y: auto;

  @media only screen and (max-width: ${styles.width_sm}) {
    padding: ${styles.spacing_standard_large} ${styles.spacing_standard} 0;
  }
`;

const RulesBlock = styled.div`
  width: 100%;
  max-width: ${styles.width_lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${styles.spacing_standard};
`;

const RulesCopy = styled.div`
  width: 100%;
  max-width: ${styles.width_sm};
`;

const RuleCardWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${styles.spacing_standard};
  padding: ${styles.spacing_standard_half} 0 ${styles.spacing_standard};
`;

const RuleCard = styled.div`
  width: 100%;
  max-width: 250px;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: ${styles.box_shadow};
  padding: ${styles.spacing_standard} ${styles.spacing_standard} 6px;

  @media only screen and (max-width: ${styles.width_sm}) {
    max-width: unset;
  }
  ${({ half }) =>
    half &&
    css`
      width: calc(50% - ${styles.spacing_standard});
      max-width: unset;
      @media only screen and (max-width: ${styles.width_md}) {
        width: 100%;
      }
    `}
`;

const TagWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  gap: ${styles.spacing_standard_half};
`;

const Tag = styled.div`
  margin-bottom: 10px;
  border-radius: 5px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: ${styles.font_sans};
  font-weight: ${styles.font_bold};
  color: ${styles.color_white};
  text-transform: uppercase;
  line-height: 1;
  background-color: ${(p) => p.bg || styles.color_white};
`;

export default function Rules({ createAGame }) {
  return (
    <RulesWrapper>
      <Header
        type="h1"
        align="center"
        padding={`0 0 ${styles.spacing_standard_large}`}
      >
        How to Play
      </Header>
      <Body align="center">
        {createAGame ? (
          <RulesBlock>
            <Header type="h3" align="center">
              Create a Lobby
            </Header>
            <RulesCopy>
              <p>
                Create a lobby and share the code or link to invite players!
                Everyone plays on their own device or browser. You need at least
                4 players to start a game, but 6+ is recommended.
              </p>
            </RulesCopy>
          </RulesBlock>
        ) : null}
        <RulesBlock>
          <Header type="h3" align="center">
            Roles
          </Header>
          <RulesCopy>
            <p>
              Each player is secretly assigned a random role. Your role's
              alignment determines your team and how you win.
            </p>
          </RulesCopy>
          <RuleCardWrapper>
            <RuleCard>
              <TagWrapper>
                <Tag bg={styles.color_green}>Camper</Tag>
              </TagWrapper>
              <p>This team wins when all villains have been killed.</p>
            </RuleCard>
            <RuleCard>
              <TagWrapper>
                <Tag bg={styles.color_red}>Villain</Tag>
              </TagWrapper>
              <p>
                This team wins when they equal or outnumber the other living
                players.
              </p>
            </RuleCard>
            <RuleCard>
              <TagWrapper>
                <Tag bg={styles.color_purple}>Unique</Tag>
              </TagWrapper>
              <p>
                These roles win when they meet their own unique win condition.
              </p>
            </RuleCard>
          </RuleCardWrapper>
        </RulesBlock>
        <RulesBlock>
          <Header type="h3" align="center">
            Gameplay
          </Header>
          <RulesCopy>
            <p>The game alternates between night and day.</p>
          </RulesCopy>
          <RuleCardWrapper>
            <RuleCard half>
              <TagWrapper>
                <Tag bg={styles.color_accent_light}>Night</Tag>
              </TagWrapper>
              <p>
                Each night, the villains secretly vote to kill one player and
                can see each other’s votes. Some roles also have abilities that
                can be used during the night.
              </p>
            </RuleCard>
            <RuleCard half>
              <TagWrapper>
                <Tag bg="#87c5ff">Day</Tag>
              </TagWrapper>
              <p>
                Each day, all living players can vote to kill one player.
                Campers try to identify and kill the villains, while the
                villains lie to protect their identities and stay alive.
              </p>
            </RuleCard>
          </RuleCardWrapper>
        </RulesBlock>
        <RulesBlock>
          <Header type="h3" align="center">
            Voting
          </Header>
          <RulesCopy>
            <p>
              When the villains or group vote to kill a player, the player with
              the most votes dies. If there's a tie, no one dies.
            </p>
          </RulesCopy>
        </RulesBlock>
        <RulesBlock>
          <Header type="h3" align="center">
            Deaths
          </Header>
          <RulesCopy>
            <p>
              When a player dies their role is revealed. Dead players cannot
              vote or use their abilities, but they can still win if their team
              wins, or if their win condition is met.
            </p>
          </RulesCopy>
        </RulesBlock>
        <RulesBlock>
          <Header type="h3" align="center">
            Winning the Game
          </Header>
          <RulesCopy>
            <p>
              The game ends when a team, or a player with a unique role, meets
              their win condition.
            </p>
          </RulesCopy>
        </RulesBlock>
      </Body>
    </RulesWrapper>
  );
}
